import { useState } from "react";
import { useQuery, useMutation } from "@apollo/client";
import axios from "axios";
import { LocalStorage } from "@/types/enums";
import { UploadedDocument } from "@/types/document";
import { FETCH_COMPANY_UPLOADS } from "@company/graphql/queries/fetchCompanyUploads";
import { DELETE_COMPANY_UPLOAD } from "@company/graphql/mutations/deleteCompanyUpload";
import { useDispatch } from "react-redux";
import { handleErrorWithNotification, showSuccessNotification } from "@/utils/errorHandling";

/**
 * Reusable hook that encapsulates document upload, deletion and retrieval for a company.
 */
export const useCompanyUploads = (companyId: string) => {
  const dispatch = useDispatch();

  // Query already uploaded documents
  const {
    data: documentsData,
    loading: documentsLoading,
    refetch: refetchDocuments,
  } = useQuery<{ companyUploads: UploadedDocument[] }>(FETCH_COMPANY_UPLOADS, {
    variables: { companyId },
    skip: !companyId,
    fetchPolicy: "network-only",
  });

  const documents = documentsData?.companyUploads || [];

  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  const [deleteDocument] = useMutation(DELETE_COMPANY_UPLOAD);

  // Upload selected files
  const uploadFiles = async (files: FileList) => {
    if (!companyId) {
      setUploadError("Keine Firma ausgewählt.");
      return;
    }

    const formData = new FormData();
    Array.from(files).forEach((file) => {
      if (file.type === "application/pdf") {
        formData.append("documents", file);
      }
    });

    if (formData.getAll("documents").length === 0) {
      setUploadError("Nur PDF-Dateien sind erlaubt.");
      return;
    }

    setUploading(true);
    setUploadError(null);

    try {
      const authToken = localStorage.getItem(LocalStorage.AUTH_TOKEN);
      if (!authToken) {
        setUploadError("Nicht authentifiziert. Bitte melden Sie sich erneut an.");
        return;
      }

      const formattedToken = `Bearer ${authToken}`;
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL || "http://localhost:4000"}/upload/${companyId}`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: formattedToken,
          },
        }
      );

      if (response.data.success) {
        showSuccessNotification("Dateien wurden erfolgreich hochgeladen", dispatch);
        refetchDocuments();
      } else {
        setUploadError("Upload fehlgeschlagen: " + response.data.error);
      }
    } catch (error) {
      setUploadError("Upload fehlgeschlagen. Bitte versuchen Sie es erneut.");
      handleErrorWithNotification(
        error as Error,
        dispatch,
        "Upload Fehler",
        "Die Dateien konnten nicht hochgeladen werden"
      );
    } finally {
      setUploading(false);
    }
  };

  // Delete document
  const handleDeleteDocument = async (filename: string) => {
    try {
      setDeleting(true);
      await deleteDocument({ variables: { companyId, filename } });
      refetchDocuments();
      showSuccessNotification("Dokument wurde gelöscht", dispatch);
    } catch (error) {
      handleErrorWithNotification(error as Error, dispatch, "Fehler", "Das Dokument konnte nicht gelöscht werden");
    } finally {
      setDeleting(false);
    }
  };

  // onchange handler for hidden file input
  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;
    uploadFiles(files);
    // reset so same file can be selected again
    event.target.value = "";
  };

  return {
    documents,
    documentsLoading,
    uploading,
    deleting,
    uploadError,
    handleFileSelect,
    handleDeleteDocument,
  } as const;
}; 